import moment from 'moment'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import keys from '@debbie/common/dist/cache/keys.js'
import {
  CaseStatus,
  Case,
  SystemEndReason,
  EndReasonId,
} from '@debbie/common/dist/cases/index.js'
import { DocumentDates } from '@debbie/common/dist/documents/index.js'
import { CaseVoucher } from '@debbie/common/dist/economy/case-vouchers/index.js'
import helpers from '@debbie/common/dist/economy/helpers/index.js'
import { expiredVouchers } from '@debbie/common/dist/economy/helpers/expiration.js'
import {
  VoucherCategory,
  VoucherTypeIds,
} from '@debbie/common/dist/economy/vouchers/index.js'
import { UpdateType } from '@debbie/common/dist/updates/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import * as caseVouchersLib from '@debbie/cortex/dist/api/case-vouchers.js'
import * as casesAPI from '@debbie/cortex/dist/api/cases.js'
import * as updatesLib from '@debbie/cortex/dist/api/updates.js'
import { selectCaseUpdates } from '@debbie/cortex/dist/case/selectors.js'
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js'
import { Button, ButtonDrop } from '@debbie/iris/dist/components/index.js'
import { DecoratedVoucher } from '@debbie/iris/dist/components/economy/case-vouchers/types.js'
import CreateVoucher from '@debbie/iris/dist/components/economy/CreateVoucher/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'

type Props = {
  singleCase: Case
  foundations: DocumentDates[]
  caseVouchers: DecoratedVoucher[]
}

const CaseEconomyHeader = (props: Props) => {
  const singleCase = props.singleCase

  const [addVoucherOpen, setAddVoucherOpen] = useState<boolean>(false)
  const [loadingExpirationMarking, setLoadingExpirationMarking] =
    useState<boolean>(false)
  const [loadingIrrecoverableMarking, setLoadingIrrecoverableMarking] =
    useState<boolean>(false)

  const lang = useSelector((s: RootState) => s.lang)
  const tenant = useSelector(selectTenant)
  const caseUpdates = useSelector(
    (s: RootState) => singleCase && selectCaseUpdates(s, singleCase.caseId),
  )
  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null

  const VouchersToBeMarkedExpired =
    props.caseVouchers &&
    creditor &&
    tenant &&
    expiredVouchers(
      helpers.vouchers.postProcessing(props.caseVouchers, {
        includeExpiredVouchers: false,
        includeDeletedVouchers: false,
        includeIrrecoverableVouchers: false,
      }),
      props.foundations,
      tenant.countryCode,
      {
        customExpiration: {
          normal: creditor.settings.customExpiration?.normal,
          foundation: creditor.settings.customExpiration?.foundation,
        },
      },
    ).filter((v) => v.category !== VoucherCategory.TRANSACTION)

  const expireCaseVouchers = async (caseVouchers: CaseVoucher[]) =>
    Promise.all(
      caseVouchers.map((caseVoucher) =>
        caseVouchersLib.patchCaseVoucher(caseVoucher.caseVoucherId, {
          caseId: caseVoucher.caseId!,
          expired: true,
        }),
      ),
    )

  const markExpired = async () => {
    if (confirm(lang.economy.sureYouWantToMarkExpired)) {
      setLoadingExpirationMarking(true)
      if (!VouchersToBeMarkedExpired) {
        return
      }

      const caseVouchersToExpire = [
        ...VouchersToBeMarkedExpired,
        ...props.caseVouchers.filter(
          (v: CaseVoucher) =>
            v.referenceCaseVoucherId &&
            VouchersToBeMarkedExpired.map((sv) => sv.caseVoucherId).includes(
              v.referenceCaseVoucherId,
            ),
        ),
      ]

      try {
        await Promise.all([
          expireCaseVouchers(caseVouchersToExpire).then(() => {
            c.caseVouchers.getByCaseId(singleCase.caseId)
            q.invalidate(
              caseVouchersToExpire.flatMap((cv) =>
                keys.write.modify(caseVouchersEntity, cv.caseVoucherId),
              ),
            )
          }),
          VouchersToBeMarkedExpired.length === props.caseVouchers.length &&
            casesAPI
              .patch(singleCase.caseId, {
                status: CaseStatus.ENDED,
                endReasonId: EndReasonId[SystemEndReason.Expiration],
              })
              .then(() => c.cases.getById(singleCase.caseId)),
          caseUpdates &&
            VouchersToBeMarkedExpired.length !== props.caseVouchers.length &&
            caseUpdates
              .filter(
                (update) =>
                  (!update.continuedAt ||
                    moment().isBefore(moment(update.continuedAt))) &&
                  update.refId &&
                  VouchersToBeMarkedExpired.map(
                    (v) => v.caseVoucherId,
                  ).includes(update.refId) &&
                  (
                    [
                      UpdateType.DEBTEXPIRED,
                      UpdateType.DEBTEXPIRESWITHINTHREEMONTHS,
                    ] as string[]
                  ).includes(update.updateTypeId),
              )
              .map((update) => updatesLib.patch(update.updateId)),
        ])

        q.invalidate(
          caseVouchersToExpire.flatMap((cv) => {
            return keys.write.modify(caseVouchersEntity, cv.caseVoucherId)
          }),
        )
      } catch (err) {
        console.error(err)
        alert('Error')
      }

      setLoadingExpirationMarking(false)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {lang.economy.caseEconomy}{' '}
      <div>
        {singleCase &&
          props.caseVouchers &&
          VouchersToBeMarkedExpired &&
          !!VouchersToBeMarkedExpired.length && (
            <Button
              style={{
                marginRight: '5px',
              }}
              theme="secondary"
              size="mini"
              onClick={markExpired}
              loading={loadingExpirationMarking}
            >
              {VouchersToBeMarkedExpired.length === props.caseVouchers.length
                ? lang.economy.markExpired
                : `${lang.economy.markSomeExpired} ${VouchersToBeMarkedExpired.length} ${lang.economy.markSomeExpiredPostings}`}
            </Button>
          )}
        <ButtonDrop
          open={addVoucherOpen}
          onOpen={() => setAddVoucherOpen(true)}
          onClose={() => setAddVoucherOpen(false)}
          size="mini"
        >
          {singleCase && (
            <CreateVoucher
              caseId={singleCase.caseId}
              creditorId={singleCase.creditorId}
              onCreate={() => setAddVoucherOpen(false)}
              voucherTypeFilter={[VoucherTypeIds.PRINCIPAL]}
              caseVouchers={props.caseVouchers}
              singleCase={props.singleCase}
            />
          )}
        </ButtonDrop>
      </div>
    </div>
  )
}

export default CaseEconomyHeader
